import {
	AfterViewInit,
	Component,
	ElementRef,
	OnDestroy,
	OnInit,
	ViewChild,
} from '@angular/core';

type GameState = 'idle' | 'playing' | 'roundCleared' | 'gameOver';

interface Bullet {
	id: number;
	left: number;
	top: number;
}

interface Enemy {
	id: number;
	left: number;
	top: number;
	type: 0 | 1 | 2;
}

interface Explosion {
	id: number;
	left: number;
	top: number;
}

@Component({
	selector: 'app-space-invaders',
	templateUrl: './space-invaders.component.html',
	styleUrls: ['./space-invaders.component.scss'],
})
export class SpaceInvadersComponent implements OnInit, AfterViewInit, OnDestroy {
	@ViewChild('gameContainer') gameContainer!: ElementRef<HTMLDivElement>;

	readonly title = 'Phil Invaders';

	gameState: GameState = 'idle';
	playerPosition = 45;
	bullets: Bullet[] = [];
	enemyBullets: Bullet[] = [];
	enemies: Enemy[] = [];
	explosions: Explosion[] = [];
	score = 0;
	round = 1;
	invaderFrame = 0;

	private nextEntityId = 1;
	private enemyDirection = 1;
	private tickInterval: ReturnType<typeof setInterval> | null = null;
	private enemyMoveInterval: ReturnType<typeof setInterval> | null = null;
	private enemyShootTimers = new Map<number, ReturnType<typeof setTimeout>>();
	private heldKeys = new Set<string>();
	private focusTimer: ReturnType<typeof setTimeout> | null = null;
	private readonly playerTop = 90;
	private readonly tickMs = 50;
	private readonly enemyMoveMs = 500;

	ngOnInit(): void {
		this.resetRoundState(false);
	}

	ngAfterViewInit(): void {
		this.focusTimer = setTimeout(() => this.focusGame(), 100);
	}

	ngOnDestroy(): void {
		this.stopAllTimers();
		if (this.focusTimer) {
			clearTimeout(this.focusTimer);
		}
	}

	focusGame(): void {
		this.gameContainer?.nativeElement?.focus();
	}

	get isPlaying(): boolean {
		return this.gameState === 'playing';
	}

	get showOverlay(): boolean {
		return this.gameState !== 'playing';
	}

	get overlayTitle(): string {
		switch (this.gameState) {
		case 'idle':
			return 'Phil Invaders';
		case 'roundCleared':
			return `Round ${this.round} cleared!`;
		case 'gameOver':
			return 'Game Over';
		default:
			return '';
		}
	}

	get overlayAction(): string {
		switch (this.gameState) {
		case 'idle':
			return 'Press Enter or tap Start';
		case 'roundCleared':
			return 'Press Enter or tap Continue';
		case 'gameOver':
			return 'Press Enter or tap Restart';
		default:
			return '';
		}
	}

	onKeyDown(event: KeyboardEvent): void {
		if (this.isMovementKey(event.key)) {
			event.preventDefault();
		}

		this.heldKeys.add(event.key);

		if (event.key === 'Enter') {
			event.preventDefault();
			this.handlePrimaryAction();
			return;
		}

		if (event.key === ' ' || event.key === 'Spacebar') {
			event.preventDefault();
			if (this.isPlaying) {
				this.shootBullet();
			}
		}
	}

	onKeyUp(event: KeyboardEvent): void {
		this.heldKeys.delete(event.key);
	}

	onTouchLeft(active: boolean): void {
		this.setTouchKey('ArrowLeft', active);
	}

	onTouchRight(active: boolean): void {
		this.setTouchKey('ArrowRight', active);
	}

	onTouchFire(): void {
		if (this.isPlaying) {
			this.shootBullet();
		}
	}

	onPrimaryAction(): void {
		this.handlePrimaryAction();
	}

	trackById(_index: number, item: { id: number }): number {
		return item.id;
	}

	startGame(): void {
		this.score = 0;
		this.round = 1;
		this.playerPosition = 45;
		this.beginRound();
	}

	startNextRound(): void {
		this.round += 1;
		this.beginRound();
	}

	restartGame(): void {
		this.startGame();
	}

	private handlePrimaryAction(): void {
		switch (this.gameState) {
		case 'idle':
			this.startGame();
			break;
		case 'roundCleared':
			this.startNextRound();
			break;
		case 'gameOver':
			this.restartGame();
			break;
		}
	}

	private beginRound(): void {
		this.resetRoundState(true);
		this.spawnEnemies();
		this.startEnemyShooting();
		this.startEnemyMovement();
		this.startTickLoop();
		this.focusGame();
	}

	private resetRoundState(clearProjectiles: boolean): void {
		this.gameState = 'idle';
		this.enemyDirection = 1;
		this.invaderFrame = 0;
		this.explosions = [];

		if (clearProjectiles) {
			this.bullets = [];
			this.enemyBullets = [];
		}

		this.stopRoundTimers();
	}

	private spawnEnemies(): void {
		this.enemies = [];
		const enemyCount = this.round * 5;
		const columns = 10;

		for (let i = 0; i < enemyCount; i += 1) {
			const row = Math.floor(i / columns);
			this.enemies.push({
				id: this.nextId(),
				left: (i % columns) * 9 + 5,
				top: row * 8 + 10,
				type: (row % 3) as 0 | 1 | 2,
			});
		}
	}

	private startTickLoop(): void {
		this.stopTickLoop();
		this.gameState = 'playing';

		this.tickInterval = setInterval(() => {
			if (!this.isPlaying) {
				return;
			}

			this.updatePlayerFromHeldKeys();
			this.updateBullets();
			this.updateEnemyBullets();
			this.cleanupExplosions();
			this.invaderFrame = (this.invaderFrame + 1) % 2;
		}, this.tickMs);
	}

	private startEnemyMovement(): void {
		this.stopEnemyMovement();

		this.enemyMoveInterval = setInterval(() => {
			if (!this.isPlaying || this.enemies.length === 0) {
				return;
			}

			let hitEdge = false;

			for (const enemy of this.enemies) {
				if (enemy.left >= 92 || enemy.left <= 3) {
					hitEdge = true;
					break;
				}
			}

			if (hitEdge) {
				this.enemyDirection *= -1;
				for (const enemy of this.enemies) {
					enemy.top += 5;
					if (enemy.top > this.playerTop) {
						this.endGame(false);
						return;
					}
				}
			}

			for (const enemy of this.enemies) {
				enemy.left += this.enemyDirection * 2;
			}
		}, this.enemyMoveMs);
	}

	private startEnemyShooting(): void {
		this.clearEnemyShootTimers();

		this.enemies.forEach((enemy) => {
			this.scheduleEnemyShot(enemy.id);
		});
	}

	private scheduleEnemyShot(enemyId: number): void {
		const delay = Math.random() * 5000 + 2000;
		const timer = setTimeout(() => {
			this.enemyShootTimers.delete(enemyId);

			if (!this.isPlaying) {
				return;
			}

			const enemy = this.enemies.find((item) => item.id === enemyId);
			if (!enemy) {
				return;
			}

			this.enemyBullets.push({
				id: this.nextId(),
				left: enemy.left,
				top: enemy.top + 4,
			});

			this.scheduleEnemyShot(enemyId);
		}, delay);

		this.enemyShootTimers.set(enemyId, timer);
	}

	private updatePlayerFromHeldKeys(): void {
		const moveKeys = ['ArrowLeft', 'ArrowRight', 'a', 'A', 'd', 'D'];
		if (!moveKeys.some((key) => this.heldKeys.has(key))) {
			return;
		}

		if (this.heldKeys.has('ArrowLeft') || this.heldKeys.has('a') || this.heldKeys.has('A')) {
			this.playerPosition = Math.max(5, this.playerPosition - 4);
		}

		if (this.heldKeys.has('ArrowRight') || this.heldKeys.has('d') || this.heldKeys.has('D')) {
			this.playerPosition = Math.min(95, this.playerPosition + 4);
		}
	}

	shootBullet(): void {
		if (!this.isPlaying) {
			return;
		}

		this.bullets.push({
			id: this.nextId(),
			left: this.playerPosition,
			top: this.playerTop - 6,
		});
	}

	private updateBullets(): void {
		const remaining: Bullet[] = [];

		for (const bullet of this.bullets) {
			const nextTop = bullet.top - 2;
			if (nextTop < 0) {
				continue;
			}

			const hitEnemyIndex = this.enemies.findIndex((enemy) =>
				this.isCollision({ left: bullet.left, top: nextTop }, enemy)
			);

			if (hitEnemyIndex >= 0) {
				const enemy = this.enemies[hitEnemyIndex];
				this.addExplosion(enemy.left, enemy.top);
				this.cancelEnemyShot(enemy.id);
				this.enemies.splice(hitEnemyIndex, 1);
				this.score += 10;
				continue;
			}

			remaining.push({ ...bullet, top: nextTop });
		}

		this.bullets = remaining;

		if (this.enemies.length === 0) {
			this.endRound();
		}
	}

	private updateEnemyBullets(): void {
		const remaining: Bullet[] = [];

		for (const bullet of this.enemyBullets) {
			const nextTop = bullet.top + 2;
			if (nextTop > 100) {
				continue;
			}

			if (this.isCollision({ left: bullet.left, top: nextTop }, { left: this.playerPosition, top: this.playerTop })) {
				this.addExplosion(this.playerPosition, this.playerTop);
				this.endGame(true);
				return;
			}

			remaining.push({ ...bullet, top: nextTop });
		}

		this.enemyBullets = remaining;
	}

	private endRound(): void {
		this.gameState = 'roundCleared';
		this.stopRoundTimers();
	}

	private endGame(playerHit: boolean): void {
		this.gameState = 'gameOver';
		this.stopRoundTimers();

		if (playerHit) {
			this.addExplosion(this.playerPosition, this.playerTop);
		}
	}

	private addExplosion(left: number, top: number): void {
		this.explosions.push({
			id: this.nextId(),
			left,
			top,
		});
	}

	private cleanupExplosions(): void {
		if (this.explosions.length > 8) {
			this.explosions = this.explosions.slice(-8);
		}
	}

	private cancelEnemyShot(enemyId: number): void {
		const timer = this.enemyShootTimers.get(enemyId);
		if (timer) {
			clearTimeout(timer);
			this.enemyShootTimers.delete(enemyId);
		}
	}

	private isCollision(
		object1: { left: number; top: number },
		object2: { left: number; top: number }
	): boolean {
		return Math.abs(object1.left - object2.left) < 5 && Math.abs(object1.top - object2.top) < 5;
	}

	private setTouchKey(key: string, active: boolean): void {
		if (active) {
			this.heldKeys.add(key);
			return;
		}
		this.heldKeys.delete(key);
	}

	private isMovementKey(key: string): boolean {
		return ['ArrowLeft', 'ArrowRight', ' ', 'Spacebar'].includes(key);
	}

	private nextId(): number {
		const id = this.nextEntityId;
		this.nextEntityId += 1;
		return id;
	}

	private stopTickLoop(): void {
		if (this.tickInterval) {
			clearInterval(this.tickInterval);
			this.tickInterval = null;
		}
	}

	private stopEnemyMovement(): void {
		if (this.enemyMoveInterval) {
			clearInterval(this.enemyMoveInterval);
			this.enemyMoveInterval = null;
		}
	}

	private clearEnemyShootTimers(): void {
		this.enemyShootTimers.forEach((timer) => clearTimeout(timer));
		this.enemyShootTimers.clear();
	}

	private stopRoundTimers(): void {
		this.stopTickLoop();
		this.stopEnemyMovement();
		this.clearEnemyShootTimers();
		this.heldKeys.clear();
	}

	private stopAllTimers(): void {
		this.stopRoundTimers();
	}
}
